// MACHINERY V2 (Stroje) — SR detail page (M2.6).
//
// Route: `/machinery-v2/:id/service-reports/:reportNumber`. Spec: data package
// §4 / §8 (the read model + its gates). Reference impl:
// design_handoff_machinery_v3/machinery-sr.jsx `SrDetailScreen`; artboards 0700
// (Refined Claude) / 0701 (MachineID Native). Desktop grid, from the handoff:
// `260px minmax(0, 1fr) 300px` — machine · body · metadata.
//
// >>> THE REPORT IS READ THROUGH AN IDENTITY GUARD. <<<
// `customerServiceReports.serviceReport` is an APP-WIDE slot that nothing clears
// on mount: `getServiceReport.fulfilled` simply assigns the newest payload
// (customerMachineServiceReportsSlice.ts:139-142). Between navigating report A
// -> B and B's response landing, the slot still holds A — and A's actions would
// be live under B's URL. The data here CARRIES its identity, so the comparison
// is inline (`report.number === reportNumber`), which is the same shape
// MachineProfileV2.tsx:137-142 and SpecificationsTabV2.tsx:84 use. No slice
// reset: that slice is shared with the LIVE v1 pages (useMachineScope.ts).
//
// SIDE EFFECT WORTH KNOWING: this page POPULATES that slot, which is what the
// create page reads to decide create-vs-edit. Every v2 entry point into the SR
// form therefore goes through srNavigation.ts, whose `openSrCreate` clears it
// first — the carried M2.5 fix.
//
// FETCH FAILURE IS VISIBLE (the §7 fix-5 pattern, extended to this page). v1
// paints "report not found" for a rejected fetch as readily as for a genuine
// 404 (`!loading && !serviceReport`, ServiceReportDetailView.tsx:262). The
// outcome is tracked per report number here instead, so a network failure
// offers RetryNotice and only a settled-but-empty fetch says "not found".
//
// ONLY THE NEWEST REQUEST MAY WRITE THAT OUTCOME (M2.6 review C10). Report A ->
// B, with A landing last, used to let A's `.then` overwrite B's outcome; the
// outcome then carried A's number, `settled` (which compares it to the CURRENT
// number) went false, and the page sat on the loading notice with no retry.
// A monotonic `requestRef` token now ignores superseded fulfilments — the same
// mechanism, for the same reason, as MachineProfileV2.tsx:102-123 (M2.4 review).
// It does not change the SHARED slot's own last-writer-wins semantics
// (customerMachineServiceReportsSlice.ts:139-142); that is exactly what the
// `report.number === number` identity guard above is there to absorb.
//
// THE MACHINE CARD'S TRACK IS RESERVED, NOT CONDITIONAL (M2.6 review R5). The
// desktop grid declares `260px` unconditionally, but the card needs the MACHINE,
// which arrives on a second fetch — usually after the report, and never if that
// fetch rejects. Rendering nothing in that cell pushed the report body into the
// 260px track. `SrMachineCardSkeleton` holds the column instead: no reflow to
// two tracks (the artboard has three), no layout shift when the card lands, and
// no collapse if it never does. The `machine.id === machineId` test that widens
// this window is the M2.5 identity discipline applied correctly — the shared
// `customerMachinery.machine` slot may still hold the PREVIOUS machine — so the
// layout holds the space rather than the guard being weakened.
//
// ACTIONS AND GATES ARE THE SR LIST'S — `buildSrActions` + `useSrTabActions`
// from M2.5, so the two v2 SR surfaces cannot disagree. Two ids are filtered:
// `details` (self-referential here) and `delete` (v1's detail bar has none; the
// list kebab owns it, and adding one would be new Admin surface).
//
// This file owns the ROUTE. The centre column is SrDetailMain.tsx, the right
// column SrDetailMeta.tsx (300-line cap).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import accountContext from "../../../../account/accountContext";
import { Modules } from "../../../../core/models/PermissionModels";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import usePermission from "../../../../shared/hooks/usePermission";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import {
  getMachineParts,
  getServiceReport,
} from "../../customerMachineServiceReportsSlice";
import { getMachine } from "../../customerMachinerySlice";
import { ServiceReportFileTypeCode } from "../../models/ServiceReportsModel";
import MachineryToastHost from "../feedback/MachineryToastHost";
import RetryNotice from "../feedback/RetryNotice";
import { Notice } from "../fleet/FleetNotices";
import { buildSrActions } from "../profile/tabs/srTabActions";
import { useSrTabActions } from "../profile/tabs/useSrTabActions";
import SrShareDialogV2 from "../profile/tabs/SrShareDialogV2";
import SrDetailMain from "./SrDetailMain";
import SrDetailMeta from "./SrDetailMeta";
import SrMachineCard, { SrMachineCardSkeleton } from "./SrMachineCard";
import SrPageBreadcrumb from "./SrPageBreadcrumb";
import { MACHINERY_V2_BASE } from "./srNavigation";

/** Entries the footer bar does not carry — see the header. */
const NOT_ON_DETAIL = ["details", "delete"];

const SrDetailV2: React.FC = () => {
  const { id, reportNumber } = useParams();
  const machineId = id || "";
  const number = reportNumber || "";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);
  const { isPhone, isTablet, isDesktop } = useBreakpoint();
  const { canWrite, canAdmin } = usePermission(Modules.Machinery);

  const machine = useAppSelector((s) => s.customerMachinery.machine);
  const report = useAppSelector((s) => s.customerServiceReports.serviceReport);
  const machineParts = useAppSelector((s) => s.customerServiceReports.machineParts);

  /** Settled outcome for THIS report number: "loaded" or "failed". */
  const [outcome, setOutcome] = useState<{ number: string; failed: boolean } | null>(null);
  /** Monotonic request token — only the NEWEST fetch may write the outcome. */
  const requestRef = useRef(0);

  const load = useCallback(() => {
    if (!number) return;
    const token = ++requestRef.current;
    setOutcome(null);
    dispatch(getServiceReport(number))
      .unwrap()
      .then(() => {
        if (requestRef.current === token) setOutcome({ number, failed: false });
      })
      .catch(() => {
        if (requestRef.current === token) setOutcome({ number, failed: true });
      });
  }, [dispatch, number]);

  useEffect(() => {
    if (machineId) dispatch(getMachine(machineId));
    dispatch(getMachineParts());
  }, [machineId, dispatch]);

  useEffect(load, [load]);

  const { handlers, shareTarget, closeShare } = useSrTabActions(machineId, load);

  /** The shared slot provably holds THIS report. */
  const current = report && report.number === number ? report : undefined;
  const settled = !!outcome && outcome.number === number;
  const failed = settled && outcome.failed;

  const affectedPartName = useMemo(() => {
    if (!current?.affectedPartCategoryId || !machineParts) return undefined;
    for (const group of machineParts) {
      const part = (group.parts || []).find((p) => p.id === current.affectedPartCategoryId);
      if (part) return part.name;
    }
    return undefined;
  }, [machineParts, current]);

  const files = current?.files || [];
  const reportFiles = files.filter((f) => f.typeCode === ServiceReportFileTypeCode.reportDocument);
  const solvingFiles = files.filter(
    (f) => f.typeCode === ServiceReportFileTypeCode.solvingReportDocument
  );

  const actions = useMemo(() => {
    if (!current) return [];
    const gates = { canWrite, canAdmin, currentUserId: accountContext.user?.id };
    return buildSrActions(current, gates, handlers).filter(
      (a) => NOT_ON_DETAIL.indexOf(a.id) < 0
    );
  }, [current, canWrite, canAdmin, handlers]);

  const machineForCard = machine && machine.id === machineId ? machine : undefined;
  const profilePath = `${MACHINERY_V2_BASE}/${machineId}`;
  const columns = isPhone ? "1fr" : isTablet ? "minmax(0, 1fr) 300px" : "260px minmax(0, 1fr) 300px";

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}>
      <SrPageBreadcrumb
        machineId={machineId}
        machineName={machineForCard ? getMachineDisplayName(machineForCard) : ""}
        current={"#" + number}
        currentMono
      />

      <main
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          padding: isPhone ? "12px 14px 24px" : "20px 24px 28px",
        }}
      >
        {failed && (
          <div style={{ marginBottom: 14 }}>
            <RetryNotice
              title={translate("myMachinery:v2.sr.loadFailed")}
              hint={translate("myMachinery:v2.tabs.loadFailedHint")}
              onRetry={load}
              busy={!settled}
            />
          </div>
        )}

        {!current && !failed && (
          <Notice
            text={
              settled
                ? translate("myMachinery:srDetail.notFound")
                : translate("myMachinery:v2.profile.loading")
            }
            dashed={settled}
          />
        )}

        {/* Tablet and phone lift the machine card ABOVE the grid rather than
            losing it: the handoff's three columns do not fit under 1280, and
            dropping the card would drop a surface (§15 reduces, it does not
            delete). */}
        {current && machineForCard && !isDesktop && (
          <div style={{ marginBottom: isPhone ? 14 : 20, minWidth: 0 }}>
            <SrMachineCard machine={machineForCard} onOpenProfile={() => navigate(profilePath)} />
          </div>
        )}

        {current && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: columns,
              gap: isPhone ? 14 : 20,
              alignItems: "start",
              minWidth: 0,
            }}
          >
            {/* The 260px track is ALWAYS filled on desktop — see the R5 note in
                the header. The card when we have the machine, its skeleton
                while we do not (including "the machine fetch failed"). */}
            {isDesktop &&
              (machineForCard ? (
                <SrMachineCard
                  machine={machineForCard}
                  onOpenProfile={() => navigate(profilePath)}
                />
              ) : (
                <SrMachineCardSkeleton />
              ))}

            <SrDetailMain
              report={current}
              reportFiles={reportFiles}
              solvingFiles={solvingFiles}
              actions={actions}
              onBack={() => navigate(profilePath)}
            />

            <SrDetailMeta report={current} affectedPartName={affectedPartName} />
          </div>
        )}
      </main>

      {shareTarget && (
        <SrShareDialogV2
          report={shareTarget}
          open
          onOpenChange={(open) => {
            if (!open) closeShare();
          }}
        />
      )}

      <MachineryToastHost />
    </div>
  );
};

export default SrDetailV2;
